import type {QueryClient} from '@github-ui/react-query'
import {type JSONRequestInit, verifiedFetchJSON} from '@github-ui/verified-fetch'

import {LABELS, VALUES} from '../constants'
import {parseFetchError, SAVED_VIEW_QUERY_KEY, SAVED_VIEWS_QUERY_KEY} from '../queries'
import type {SavedView, SavedViewId, SavedViewsResponse, SetSavedViewPinnedVariables} from '../types'

export interface SavedViewMutationOptions {
  owner: string
  repo: string
}

interface SavedViewCacheTargets extends SavedViewMutationOptions {
  queryClient: QueryClient
}

interface SavedViewDetailCache {
  savedView: SavedView
  viewerCanManageViews: boolean
}

export const PINNED_LIMIT_EXCEEDED = 'pinned_limit_exceeded'

export class SavedViewMutationError extends Error {
  readonly status: number
  readonly code?: string

  constructor(status: number, message: string, code?: string) {
    super(message)
    this.name = 'SavedViewMutationError'
    this.status = status
    this.code = code
  }
}

export function isPinLimitError(error: unknown, attemptedPin: boolean): boolean {
  return attemptedPin && error instanceof SavedViewMutationError && error.code === PINNED_LIMIT_EXCEEDED
}

export function pinnedMutationErrorMessage(error: unknown, attemptedPin: boolean, viewName: string): string {
  if (isPinLimitError(error, attemptedPin)) return LABELS.views.pinLimitReached(VALUES.maxPinnedViews)
  return attemptedPin ? LABELS.views.pinError(viewName) : LABELS.views.unpinError(viewName)
}

export async function sendSavedViewMutation(
  path: string,
  init: JSONRequestInit,
  errorMessage: string,
): Promise<Response> {
  const response = await verifiedFetchJSON(path, init)
  if (!response.ok) {
    const {message, code} = await parseFetchError(response, errorMessage)
    throw new SavedViewMutationError(response.status, message, code)
  }
  return response
}

export async function sendSavedViewMutationJson<T>(
  path: string,
  init: JSONRequestInit,
  errorMessage: string,
): Promise<T> {
  const response = await sendSavedViewMutation(path, init, errorMessage)
  return (await response.json()) as T
}

export function primeSavedViewCache({queryClient, owner, repo}: SavedViewCacheTargets, savedView: SavedView): void {
  const detailKey = [SAVED_VIEW_QUERY_KEY, owner, repo, String(savedView.id)] as const
  const existing = queryClient.getQueryData<SavedViewDetailCache>(detailKey)
  queryClient.setQueryData<SavedViewDetailCache>(detailKey, {
    savedView,
    viewerCanManageViews: existing?.viewerCanManageViews ?? true,
  })
}

export function primeSavedViewCaches(targets: SavedViewCacheTargets, savedView: SavedView): void {
  primeSavedViewCache(targets, savedView)
  const listKey = [SAVED_VIEWS_QUERY_KEY, targets.owner, targets.repo] as const
  targets.queryClient.setQueryData<SavedViewsResponse>(listKey, current => {
    if (!current) return current

    const savedViewExists = current.savedViews.some(view => String(view.id) === String(savedView.id))
    return {
      ...current,
      savedViews: savedViewExists
        ? current.savedViews.map(view => (String(view.id) === String(savedView.id) ? savedView : view))
        : [...current.savedViews, savedView],
    }
  })
  invalidateSavedViewsList(targets)
}

export function invalidateSavedViewsList({queryClient, owner, repo}: SavedViewCacheTargets): void {
  void queryClient.invalidateQueries({queryKey: [SAVED_VIEWS_QUERY_KEY, owner, repo]})
}

export function invalidateSavedView({queryClient, owner, repo}: SavedViewCacheTargets, id: number | string): void {
  void queryClient.invalidateQueries({queryKey: [SAVED_VIEW_QUERY_KEY, owner, repo, String(id)] as const, exact: true})
}

export function removeSavedViewCaches({queryClient, owner, repo}: SavedViewCacheTargets, id: number | string): void {
  queryClient.removeQueries({queryKey: [SAVED_VIEW_QUERY_KEY, owner, repo, String(id)] as const})
  invalidateSavedViewsList({queryClient, owner, repo})
}

export interface SavedViewPinnedSnapshot {
  id: SavedViewId
  list?: {previousPinned: boolean | undefined}
  detail?: {previousPinned: boolean | undefined}
}

export async function optimisticallySetSavedViewPinned(
  {queryClient, owner, repo}: SavedViewCacheTargets,
  {id, pinned}: SetSavedViewPinnedVariables,
): Promise<SavedViewPinnedSnapshot> {
  const listKey = [SAVED_VIEWS_QUERY_KEY, owner, repo] as const
  const detailKey = [SAVED_VIEW_QUERY_KEY, owner, repo, String(id)] as const

  const listWarm = queryClient.getQueryData<SavedViewsResponse>(listKey) !== undefined
  const detailWarm = queryClient.getQueryData<SavedViewDetailCache>(detailKey) !== undefined

  await Promise.all([
    listWarm ? queryClient.cancelQueries({queryKey: listKey, exact: true}) : undefined,
    detailWarm ? queryClient.cancelQueries({queryKey: detailKey, exact: true}) : undefined,
  ])

  const snapshot: SavedViewPinnedSnapshot = {id}

  if (listWarm) {
    queryClient.setQueryData<SavedViewsResponse>(listKey, current => {
      if (!current) return current
      const target = current.savedViews.find(view => view.id === id)
      if (!target) return current
      snapshot.list = {previousPinned: target.pinned}
      if (target.pinned === pinned) return current
      return {...current, savedViews: current.savedViews.map(view => (view.id === id ? {...view, pinned} : view))}
    })
  }

  if (detailWarm) {
    queryClient.setQueryData<SavedViewDetailCache>(detailKey, current => {
      if (!current || current.savedView.id !== id) return current
      snapshot.detail = {previousPinned: current.savedView.pinned}
      if (current.savedView.pinned === pinned) return current
      return {...current, savedView: {...current.savedView, pinned}}
    })
  }

  return snapshot
}

export function restoreSavedViewPinnedSnapshot(
  {queryClient, owner, repo}: SavedViewCacheTargets,
  snapshot: SavedViewPinnedSnapshot,
): void {
  const {id} = snapshot
  if (snapshot.list) {
    const {previousPinned} = snapshot.list
    queryClient.setQueryData<SavedViewsResponse>([SAVED_VIEWS_QUERY_KEY, owner, repo] as const, current =>
      current
        ? {
            ...current,
            savedViews: current.savedViews.map(view => (view.id === id ? {...view, pinned: previousPinned} : view)),
          }
        : current,
    )
  }
  if (snapshot.detail) {
    const {previousPinned} = snapshot.detail
    queryClient.setQueryData<SavedViewDetailCache>([SAVED_VIEW_QUERY_KEY, owner, repo, String(id)] as const, current =>
      current && current.savedView.id === id
        ? {...current, savedView: {...current.savedView, pinned: previousPinned}}
        : current,
    )
  }
}
